import type { UseFetchOptions } from 'nuxt/app'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import type { StrapiMagazineArticle } from '~/composables/types/strapi/strapi-magazine'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'

const getStrapiArticlesUrl = ({
  slug,
  query,
  locale,
}: {
  slug?: string
  query?: Record<string, string>
  locale?: string
}) => {
  const articlesUrl = new URLSearchParams()

  if (slug) {
    articlesUrl.set('filters[slug][$eq]', slug)
  }

  articlesUrl.set('locale', locale || 'de-de')

  if (query) {
    Object.keys(query).forEach((key) => {
      articlesUrl.set(key, query[key])
    })
  }

  return 'articles?' + articlesUrl.toString()
}

export function useStrapiArticles({
  slug,
  query,
  options = {},
  locale,
}: {
  slug?: string
  query?: Record<string, string>
  options?: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiMagazineArticle[]>,
    StrapiMagazineArticle[]
  >
  locale?: string
  preview?: boolean
} = {}) {
  const localeDefault = useI18n().locale

  const articlesUrl = getStrapiArticlesUrl({
    slug,
    query,
    locale: locale || localeDefault.value,
  })

  const staticMetrics = useStaticMetrics()

  const baseTransform = (
    data: StrapiApiDefaultResponse<StrapiMagazineArticle[]>
  ): StrapiMagazineArticle[] => {
    return data.data.map((item) =>
      dynamicContentTransformer(item, staticMetrics)
    )
  }

  return useStrapi<StrapiMagazineArticle>({
    urlSuffix: articlesUrl,
    options: { ...options, transform: baseTransform },
  })
}
